/* Wire contract for the gateway insights read API (/insights).
 *
 * TypeScript mirror of the BFF's Pydantic `InsightResponse`
 * (ai21_intelligent_gateway_api/insights/schemas.py) — field names must match
 * exactly; treat every edit as a cross-repo contract change (webapp merges
 * first). Responses are bare JSON (no analytics Envelope). */

/** Lifecycle state. The customer list endpoint returns `published` rows only;
 *  `applied`/`acknowledged`/`dismissed` come back on the transition responses. */
export type InsightState =
  "draft" | "published" | "applied" | "acknowledged" | "dismissed";

/** Whether the insight ships a config delta we can apply for the user
 *  (`config_change`) or is free-form advice they act on themselves
 *  (`advisory`, terminal action = acknowledge). Derived by the BFF from
 *  `config_suggestion`, so it can never contradict it (OPSS-283). */
export type InsightKind = "config_change" | "advisory";

/** User verdict on an insight. Deliberately two values, not a rating scale. */
export type FeedbackVerdict = "helpful" | "not_helpful";

export interface Insight {
  /** Insight UUID — path key for the apply/dismiss transitions. */
  id: string;
  workspace_id: string;
  agent_id: string;
  state: InsightState;
  /** Which action the card offers (apply vs acknowledge). Mirrors a required
   *  BFF field, but an api-server release older than OPSS-283 strips it from
   *  the response — read it through `insightKind()` below, never bare, so a
   *  missing value degrades to `config_change` (today's behaviour). */
  kind: InsightKind;
  /** Producer of the insight (e.g. "manual" for OPSS-207 authored ones). */
  source: string;
  author: string | null;
  /** Customer-facing headline. */
  title: string;
  /** Customer-facing narrative/evidence copy. */
  narrative: string;
  /** Free-text savings figure authored with the insight (e.g. "≈22%"). */
  projected_savings: string | null;
  /** Optimization-catalog card id this insight suggests, when it maps to one. */
  optimization_id: string | null;
  /** Config-profile overrides the apply flow merges onto the agent's config. */
  config_suggestion: Record<string, unknown> | null;
  /** Last verdict the user submitted; null until they submit one, and the
   *  "is there any feedback?" marker (a verdict is required on submission).
   *  Last-write-wins — the BFF keeps no submission history (OPSS-283). */
  feedback_verdict: FeedbackVerdict | null;
  /** Optional free text submitted with the verdict. Overwritten (to null, if
   *  the submission carries none) on every submission. */
  feedback_comment: string | null;
  created_at: string;
  updated_at: string;
}

/** `kind`, defaulted for wire payloads that predate OPSS-283 (the api-server
 *  mirrors `InsightOut` field-by-field and silently drops what it doesn't
 *  declare, so `kind` is absent until PR 2 ships). Advisory is the opt-in
 *  branch; anything else keeps the original apply-able card. */
export function insightKind(insight: Insight): InsightKind {
  return insight.kind === "advisory" ? "advisory" : "config_change";
}
